// Chakra imports
import {
  Box,
  Button,
  Flex,
  Icon,
  Image,
  Link,
  Text,
  useColorModeValue,
  Tooltip,
  Tag,
  TagLabel,
  HStack,
  Wrap,
  WrapItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  IconButton,
  Input,
  Divider,
} from "@chakra-ui/react";
import { IoMdOpen } from "react-icons/io";
import React, { useMemo, useState } from "react";

import Card from "components/card/Card.js";

// Icons for abuse types
import { RiTrademarkLine } from "react-icons/ri";
import {
  MdMedicalServices,
  MdPriceChange,
  MdMoreHoriz,
  MdDone,
  MdAdd,
  MdFlag,
  MdVerified,
} from "react-icons/md";

export default function NFT(props) {
  const {
    id,
    image,
    name,
    infringingName,
    url,
    platform,
    clientStatus, // null/undefined => pending, 0 => no-infringe, 1 => infringe, 2 => reported, 3 => taken-down, -1 => irrelevant
    infringementType, // comma-separated string from backend
    date,
    // actions:
    onInfringeWithTypes, // (id, typesArray)
    onAddMoreTypes, // (id, typesArray) -> merge
    onNoInfringe,
    onIrrelevant,
    onMarkReported,
    onMarkTakenDown,
    // context:
    view, // "identified" | "infringement" | others
  } = props;

  const textColor = useColorModeValue("navy.700", "white");
  const subTextColor = useColorModeValue("gray.600", "gray.300");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.300");
  const badgeBg = useColorModeValue("gray.50", "whiteAlpha.100");

  const isInfringe = clientStatus === 1;
  const isReported = clientStatus === 2;
  const isTakenDown = clientStatus === 3;
  const isNoInfringe = clientStatus === 0;
  const isIrrelevant = clientStatus === -1;
  const isIdentified =
    clientStatus === null || typeof clientStatus === "undefined";

  // Parse comma-separated types to array (normalized)
  const typesArray = useMemo(() => {
    return (infringementType || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((t) => t.toLowerCase());
  }, [infringementType]);

  // Nice display-case helper
  const pretty = (t) =>
    t
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  // Abuse options config
  const ABUSE_OPTIONS = [
    { key: "brand abuse", label: "Brand Abuse", icon: RiTrademarkLine },
    { key: "prescription abuse", label: "Prescription Abuse", icon: MdMedicalServices },
    { key: "price abuse", label: "Price Abuse", icon: MdPriceChange },
    { key: "other", label: "Other", icon: MdMoreHoriz },
  ];

  // Popover state for selecting types
  const [selectOpen, setSelectOpen] = useState(false);
  const [selected, setSelected] = useState(new Set()); // lowercased keys
  const [otherText, setOtherText] = useState("");

  const toggleKey = (k) => {
    const s = new Set(selected);
    if (s.has(k)) s.delete(k);
    else s.add(k);
    setSelected(s);
  };

  const clearSelection = () => {
    setSelected(new Set());
    setOtherText("");
  };

  const handleDone = async () => {
    // Build types array. If none selected -> send [] so parent sends ""
    let out = Array.from(selected);
    if (out.includes("other")) {
      const t = (otherText || "").trim();
      // If user wants "Other" AND provided text, append custom; if they didn't provide text, we ignore it
      if (t.length) out = out.filter((x) => x !== "other").concat([t.toLowerCase()]);
      else out = out.filter((x) => x !== "other"); // no text -> don't include "other"
    }

    // Fire action
    if (view === "identified" || (!isInfringe && !isReported && !isTakenDown)) {
      // Mark as infringement (status = 1) with (optional) types
      await onInfringeWithTypes?.(id, out);
    } else {
      // We're in infringement view adding more types
      await onAddMoreTypes?.(id, out);
    }

    clearSelection();
    setSelectOpen(false);
  };

  // Show status tag top-right
  const statusTag = useMemo(() => {
    if (isTakenDown)
      return <Tag position="absolute" top="10px" right="10px" colorScheme="green" variant="solid">Taken Down</Tag>;
    if (isReported)
      return <Tag position="absolute" top="10px" right="10px" colorScheme="orange" variant="solid">Reported</Tag>;
    if (isInfringe)
      return <Tag position="absolute" top="10px" right="10px" colorScheme="red" variant="subtle">Infringement</Tag>;
    return null;
  }, [isInfringe, isReported, isTakenDown]);

  return (
    <Card p="16px" w="100%" position="relative">
      {statusTag}

      <Box>
        <Link target="_blank" href={url}>
          <Flex direction="column" justify="center">
            {/* Image */}
            <Box
              mb="5px"
              display="flex"
              justifyContent="center"
              borderRadius="16px"
              borderWidth="1px"
              borderColor={borderColor}
              overflow="hidden"
              bg={badgeBg}
            >
              <Image
                src={image}
                alt={name}
                h="220px"
                w="100%"
                objectFit="contain"
                fallbackSrc={image}
              />
            </Box>

            {/* Texts */}
            <Flex direction="column" gap={0}>
              <Flex direction="row" gap={6} >

                <Link target="_blank" width="100%" href={platform}>
                  <Text color={subTextColor} fontSize="sm">
                    {(platform || "").replace("www.", "")}
                  </Text>
                </Link>
                <Flex width="100%" >
                  <Text color={subTextColor} fontSize="sm">
                    {(date || "")}
                  </Text>
                </Flex>
               
              </Flex>
              <Text color={textColor} fontSize="md" fontWeight="700">
                <Box as="span" fontWeight="800">
                  {name}
                </Box>
              </Text>
              <Text color={subTextColor} fontSize="sm">
                {infringingName || ""}
              </Text>
            </Flex>
          </Flex>
        </Link>
      </Box>

      {/* Infringement types badges (only in infringement view & when any exist) */}
      {view === "infringement" && typesArray.length > 0 && (
        <Wrap mt={3}>
          {typesArray.map((t, idx) => (
            <WrapItem key={idx}>
              <Tag size="sm" variant="subtle" colorScheme="red">
                <TagLabel>{pretty(t)}</TagLabel>
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}

      {/* Actions */}
      <Flex mt={4} gap={3} wrap="wrap" align="center">
        {/* Popover for selecting infringement types (used in multiple places) */}
        <Popover
          isOpen={selectOpen}
          onClose={() => setSelectOpen(false)}
          placement="bottom-start"
          isLazy
          closeOnBlur
        >
          <PopoverTrigger>
            {/* Trigger differs by state/view */}
            {isIdentified || (!isInfringe && !isReported && !isTakenDown) ? (
              <Button
                colorScheme="red"
                size="sm"
                onClick={() => setSelectOpen(!selectOpen)}
              >
                Infringement Listing
              </Button>
            ) : (
              // In Infringement view we show a small + Add Types icon above the other buttons
              view === "infringement" && (
                <Tooltip label="Add Infringement Types" hasArrow>
                  <IconButton
                    aria-label="Add Infringement Types"
                    icon={<MdAdd />}
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectOpen(!selectOpen)}
                  />
                </Tooltip>
              )
            )}
          </PopoverTrigger>
          <PopoverContent w="auto" maxW="320px">
            <PopoverArrow />
            <PopoverBody>
              <Text fontWeight="600" mb={2}>Select infringement types</Text>
              <HStack spacing={2} wrap="wrap">
                {ABUSE_OPTIONS.map((opt) => (
                  <Tooltip key={opt.key} label={opt.label} hasArrow>
                    <IconButton
                      aria-label={opt.label}
                      icon={<opt.icon />}
                      size="sm"
                      variant={selected.has(opt.key) ? "solid" : "outline"}
                      colorScheme={selected.has(opt.key) ? "red" : "gray"}
                      onClick={() => toggleKey(opt.key)}
                    />
                  </Tooltip>
                ))}
                {/* Done */}
                <Tooltip label="Done" hasArrow>
                  <IconButton
                    aria-label="Done"
                    icon={<MdDone />}
                    size="sm"
                    colorScheme="green"
                    onClick={handleDone}
                  />
                </Tooltip>
              </HStack>

              {/* Other input */}
              {selected.has("other") && (
                <>
                  <Divider my={3} />
                  <Input
                    size="sm"
                    placeholder="Describe other type (optional)"
                    value={otherText}
                    onChange={(e) => setOtherText(e.target.value)}
                  />
                  <Text mt={2} fontSize="xs" color={subTextColor}>
                    If you leave this blank, “Other” won’t be added.
                  </Text>
                </>
              )}
            </PopoverBody>
          </PopoverContent>
        </Popover>

        {/* The rest of the action buttons depend on state */}
        {/* Identified state: allow No Infringe / Irrelevant */}
        {isIdentified && (
          <>
            <Button
              colorScheme="green"
              size="sm"
              variant="outline"
              onClick={() => onNoInfringe?.(id)}
            >
              No Infringe
            </Button>
            <Button
              colorScheme="yellow"
              size="sm"
              variant="outline"
              onClick={() => onIrrelevant?.(id)}
            >
              Irrelevant
            </Button>
          </>
        )}

        {/* Infringement tab actions */}
        {view === "infringement" && (isInfringe || isReported || isTakenDown) && (
          <>
            {/* When status=1 (Infringement not reported) -> show Report button */}
            {isInfringe && (
              <Tooltip label="Mark as Reported" hasArrow>
                <Button
                  size="sm"
                  colorScheme="orange"
                  leftIcon={<MdFlag />}
                  onClick={() => onMarkReported?.(id)}
                >
                  Reported
                </Button>
              </Tooltip>
            )}

            {/* When status=2 (Reported) -> show Taken Down button */}
            {isReported && (
              <Tooltip label="Mark as Taken Down" hasArrow>
                <Button
                  size="sm"
                  colorScheme="green"
                  leftIcon={<MdVerified />}
                  onClick={() => onMarkTakenDown?.(id)}
                >
                  Taken Down
                </Button>
              </Tooltip>
            )}

            {/* Still allow switching to No Infringe / Irrelevant from any infringe state */}
            <Button
              colorScheme="green"
              size="sm"
              variant="outline"
              onClick={() => onNoInfringe?.(id)}
            >
              No Infringe
            </Button>
            <Button
              colorScheme="yellow"
              size="sm"
              variant="outline"
              onClick={() => onIrrelevant?.(id)}
            >
              Irrelevant
            </Button>
          </>
        )}

        {/* No Infringe state card (outside infringement tab) */}
        {isNoInfringe && view !== "infringement" && (
          <>

            <Button
              colorScheme="yellow"
              size="sm"
              variant="outline"
              onClick={() => onIrrelevant?.(id)}
            >
              Irrelevant
            </Button>
          </>
        )}

        {/* Irrelevant state card (outside infringement tab) */}
        {isIrrelevant && view !== "infringement" && (
          <>

            <Button
              colorScheme="green"
              size="sm"
              variant="outline"
              onClick={() => onNoInfringe?.(id)}
            >
              No Infringe
            </Button>
          </>
        )}
      </Flex>
    </Card>
  );
}
